import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  CanMatchFn,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree,
} from '@angular/router';
import { AuthenticationService, PermissionHelperService } from '@core/services';

function buildUrlFromRoute(route: Route, segments: UrlSegment[]): string {
  return `/${[route.path, ...segments.map((segment) => segment.path)].filter(Boolean).join('/')}`;
}

function authRedirect(returnUrl: string): UrlTree {
  const router = inject(Router);
  return router.createUrlTree(['/login'], { queryParams: { returnUrl } });
}

function homeRedirect(): UrlTree {
  const router = inject(Router);
  const permissionHelper = inject(PermissionHelperService);
  return router.createUrlTree([permissionHelper.resolveHomeUrl()]);
}

export const guestOnlyCanActivate: CanActivateFn = () => {
  const authenticationService = inject(AuthenticationService);
  return authenticationService.isAuthenticated() ? homeRedirect() : true;
};

export const guestOnlyCanMatch: CanMatchFn = () => {
  const authenticationService = inject(AuthenticationService);
  return authenticationService.isAuthenticated() ? homeRedirect() : true;
};

export const centralOnlyCanActivate: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authenticationService = inject(AuthenticationService);
  if (!authenticationService.isAuthenticated()) {
    return authRedirect(state.url);
  }
  return authenticationService.isTenant() ? homeRedirect() : true;
};

export const centralOnlyCanMatch: CanMatchFn = (route: Route, segments: UrlSegment[]) => {
  const authenticationService = inject(AuthenticationService);
  if (!authenticationService.isAuthenticated()) {
    return authRedirect(buildUrlFromRoute(route, segments));
  }
  return authenticationService.isTenant() ? homeRedirect() : true;
};

export const tenantOnlyCanActivate: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authenticationService = inject(AuthenticationService);
  if (!authenticationService.isAuthenticated()) {
    return authRedirect(state.url);
  }
  return authenticationService.isTenant() ? true : homeRedirect();
};

export const tenantOnlyCanMatch: CanMatchFn = (route: Route, segments: UrlSegment[]) => {
  const authenticationService = inject(AuthenticationService);
  if (!authenticationService.isAuthenticated()) {
    return authRedirect(buildUrlFromRoute(route, segments));
  }
  return authenticationService.isTenant() ? true : homeRedirect();
};

export const selfManagementCanActivate: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authenticationService = inject(AuthenticationService);
  const permissionHelper = inject(PermissionHelperService);
  if (!authenticationService.isAuthenticated()) {
    return authRedirect(state.url);
  }
  if (!authenticationService.isTenant()) {
    return homeRedirect();
  }
  return permissionHelper.isSelfManagement() ? true : homeRedirect();
};

export const selfManagementCanMatch: CanMatchFn = (route: Route, segments: UrlSegment[]) => {
  const authenticationService = inject(AuthenticationService);
  const permissionHelper = inject(PermissionHelperService);
  if (!authenticationService.isAuthenticated()) {
    return authRedirect(buildUrlFromRoute(route, segments));
  }
  if (!authenticationService.isTenant()) {
    return homeRedirect();
  }
  return permissionHelper.isSelfManagement() ? true : homeRedirect();
};
